import React from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
// Waitlist deps — kept for the commented-out Waitlist flow below in case we bring it back.
// import { useState } from 'react';
// import { db } from './services/firebaseConfig';
// import { collection, addDoc, where, getDocs, query, serverTimestamp } from 'firebase/firestore';
import { Analytics } from '@vercel/analytics/react';
import './App.css';

const USER_COUNT = '500+';
const APP_STORE_URL = 'https://apps.apple.com/app/id6770390046';
// const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Nav() {
  const location = useLocation();
  const isActive = (path) => location.pathname === path;

  return (
    <nav className="nav">
      <Link to="/" className={`nav-link ${isActive('/') ? 'active' : ''}`}>home</Link>
      <Link to="/about" className={`nav-link ${isActive('/about') ? 'active' : ''}`}>about</Link>
      <a href={APP_STORE_URL} className="nav-link" target="_blank" rel="noopener noreferrer">join</a>
      <Link to="/support" className={`nav-link ${isActive('/support') ? 'active' : ''}`}>support</Link>
    </nav>
  );
}

function Layout({ children }) {
  return (
    <div className="page">
      <header className="topbar">
        <Link to="/" className="wordmark-link">
          <img src="/wordmark.svg" alt="citymaps" className="wordmark" />
        </Link>
        <Nav />
      </header>

      <main className="center">
        {children}
      </main>

      <footer className="bottombar">
        <div className="footer-left">
          <span className="copyright">&copy; citymaps inc.</span>
          <Link to="/privacy" className="footer-link">privacy</Link>
          <Link to="/terms" className="footer-link">terms</Link>
        </div>
        <div className="links">
          <a href="https://instagram.com/citymapsapp" className="handle" target="_blank" rel="noopener noreferrer">@citymapsapp</a>
          <a href="mailto:support@citymaps.app" className="email-link">support@citymaps.app</a>
        </div>
      </footer>

      <Analytics />
    </div>
  );
}

function Home() {
  return (
    <Layout>
      <h1 className="headline">new york, rated by your friends.</h1>
      <p className="subtext">discover the best spots in nyc through the people you trust.</p>

      <div className="home-buttons">
        <a href={APP_STORE_URL} className="submit home-btn" target="_blank" rel="noopener noreferrer">join citymaps</a>
        <Link to="/about" className="submit home-btn">about</Link>
        <Link to="/support" className="submit home-btn">support</Link>
      </div>

      <div className="proof">
        <span className="dot" />
        <span className="proof-text">{USER_COUNT} users</span>
      </div>
    </Layout>
  );
}

// Pre-launch waitlist flow. Disabled now that the app is live (join links point to
// the App Store). Kept here, along with its imports/EMAIL_RE above and the /join route
// below, in case we want to bring it back.
/*
function Waitlist() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const clearError = () => {
    if (status === 'error') {
      setStatus('idle');
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedName || !EMAIL_RE.test(trimmedEmail)) {
      setError('something went wrong. try again?');
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setError('');

    try {
      const q = query(collection(db, 'waitlist'), where('email', '==', trimmedEmail));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        await addDoc(collection(db, 'waitlist'), {
          name: trimmedName,
          email: trimmedEmail,
          timestamp: serverTimestamp(),
        });
      }

      setStatus('success');
      setName('');
      setEmail('');
    } catch (err) {
      setError('something went wrong. try again?');
      setStatus('error');
    }
  };

  return (
    <Layout>
      <h1 className="headline">join citymaps.</h1>
      <p className="subtext">sign up and we'll get you started.</p>

      {status === 'success' ? (
        <p className="confirm">you're in. we'll be in touch.</p>
      ) : (
        <form className="form" onSubmit={handleSubmit} noValidate>
          <div className="inputs">
            <input
              type="text"
              className="input name"
              placeholder="your name"
              aria-label="your name"
              value={name}
              onChange={(e) => { setName(e.target.value); clearError(); }}
              autoComplete="given-name"
            />
            <input
              type="email"
              className="input email"
              placeholder="your email"
              aria-label="email address"
              value={email}
              onChange={(e) => { setEmail(e.target.value); clearError(); }}
              autoComplete="email"
              spellCheck={false}
            />
          </div>
          <button type="submit" className="submit" disabled={status === 'submitting'}>
            join citymaps
          </button>
        </form>
      )}

      {status === 'error' && error && (
        <p className="error" role="alert">{error}</p>
      )}
    </Layout>
  );
}
*/

function About() {
  return (
    <Layout>
      <h1 className="headline">about.</h1>
      <div className="page-body">
        <p>a personal map of new york, rated by your friends.</p>
        <p>no strangers. no algorithms. no paid placements.</p>
        <p>new york now. more cities soon.</p>
      </div>
    </Layout>
  );
}

function Support() {
  return (
    <Layout>
      <h1 className="headline">support.</h1>
      <div className="page-body">
        <p>
          need help or have a question? we'd love to hear from you.
        </p>
        <p>
          email us at{' '}
          <a href="mailto:support@citymaps.app" className="inline-link">support@citymaps.app</a>{' '}
          and we'll get back to you as soon as possible.
        </p>
        <p>
          you can also reach us on social media{' '}
          <a href="https://instagram.com/citymapsapp" className="inline-link" target="_blank" rel="noopener noreferrer">@citymapsapp</a>.
        </p>
      </div>
    </Layout>
  );
}

function Privacy() {
  return (
    <Layout>
      <h1 className="headline">privacy policy.</h1>
      <div className="page-body legal">
        <p className="legal-date">effective may 9, 2026</p>

        <p>this policy explains what citymaps ("we", "our", "us") collects, how we use it, and the rights you have over your data. by using the citymaps app or website, you agree to this policy.</p>

        <h2>who we are</h2>
        <p>citymaps is operated by CityMaps Inc. contact: <a href="mailto:support@citymaps.app" className="inline-link">support@citymaps.app</a>.</p>

        <h2>information we collect</h2>
        <p>we collect the following categories of information.</p>
        <p>account information you provide when you sign up or update your profile: phone number, email, name, username, profile photo, optional bio.</p>
        <p>content you create in the app: ratings, comments, photos, posts, lists, follows, saves, and reactions.</p>
        <p>search queries you type into the app.</p>
        <p>device and usage data automatically collected when you use the app: device type and model, OS version, app version, IP address, push notification token, crash logs, anonymous interaction events, and (with your permission) location and contacts.</p>
        <p>communications you send us, such as support emails or moderation reports.</p>
        <p>we do not collect race, religion, political views, sexual orientation, or financial account information.</p>

        <h2>how we collect it</h2>
        <p>we collect information directly from you when you sign up, post content, or contact us; automatically from your device while you use the app; and from service providers who help us run the service.</p>

        <h2>how we use your information</h2>
        <p>we use your information to create and operate your account, and to deliver the core features of the app: showing nearby places, surfacing friends, displaying ratings and posts, sending verification codes, and enabling notifications you opt into.</p>
        <p>we also use it to improve the app through anonymous analytics and session recording for product diagnostics, to moderate content and respond to reports of abuse, to communicate with you about service updates, security, or support, and to comply with legal obligations.</p>
        <p>we do not sell your personal information.</p>

        <h2>sharing with third parties</h2>
        <p>we use trusted service providers to run the service, including providers for hosting, authentication, analytics, push notifications, SMS delivery, and AI processing of search queries. each provider receives only the data necessary to perform its function and is bound by privacy obligations at least as protective as those described in this policy. we do not authorize any provider to use your information for their own marketing.</p>
        <p>we may share information when required by law, in response to legal process, or to protect the rights, safety, or property of citymaps, our users, or the public.</p>

        <h2>permissions you grant on the device</h2>
        <p>location: used only when you grant permission and only to surface nearby places and personalize the app. we do not track your location in the background.</p>
        <p>contacts: if you grant permission, your contacts' phone numbers are transmitted to our server only to identify which of your contacts already use citymaps. your raw contact list is not retained beyond the matching step.</p>
        <p>photos and camera: used only to upload images you choose for your profile or for places you post about.</p>
        <p>notifications: used only to send messages tied to in-app activity (likes, comments, follows). you can disable them at any time in your device settings.</p>

        <h2>data retention</h2>
        <p>we retain account and content data for as long as your account is active. backups containing personal data may persist for up to 30 days after deletion before being permanently purged.</p>

        <h2>account deletion</h2>
        <p>you can delete your account at any time from inside the app under settings. if you can't access the app, email <a href="mailto:support@citymaps.app" className="inline-link">support@citymaps.app</a> from the email associated with your account and we will delete it for you.</p>

        <h2>your rights</h2>
        <p>you may at any time access the personal data we hold about you, request correction of inaccurate data, object to or restrict certain processing, or withdraw a permission you previously granted (e.g., contacts, location) via your device settings.</p>
        <p>to exercise any of these rights, email <a href="mailto:support@citymaps.app" className="inline-link">support@citymaps.app</a> from the email associated with your account. we will respond within 30 days.</p>
        <p>if you are in the EEA, UK, or Switzerland, you also have the right to lodge a complaint with your local data protection authority.</p>

        <h2>children's privacy</h2>
        <p>citymaps is not directed to children under 13 and we do not knowingly collect personal information from children under 13. if you believe a child under 13 has provided us with personal information, contact <a href="mailto:support@citymaps.app" className="inline-link">support@citymaps.app</a> and we will delete it.</p>

        <h2>security</h2>
        <p>we use industry-standard measures to protect your information, including encryption in transit, restricted access to production systems, and secure credential storage. no system is perfectly secure; if we discover a breach affecting your personal information, we will notify affected users in accordance with applicable law.</p>

        <h2>international transfers</h2>
        <p>our service providers may process personal data in jurisdictions other than the one you live in, including the United States. where required, we rely on standard contractual clauses or other approved transfer mechanisms.</p>

        <h2>changes to this policy</h2>
        <p>we may update this policy from time to time. when we do, we will update the effective date above and, if changes are material, notify you in the app or by email. continued use of citymaps after changes take effect constitutes acceptance of the updated policy.</p>

        <h2>contact</h2>
        <p>questions, requests, or complaints: <a href="mailto:support@citymaps.app" className="inline-link">support@citymaps.app</a>.</p>
      </div>
    </Layout>
  );
}

function Terms() {
  return (
    <Layout>
      <h1 className="headline">terms of service.</h1>
      <div className="page-body legal">
        <p className="legal-date">effective may 2, 2026</p>

        <p>by using citymaps ("the app"), you agree to these terms. if you do not agree, do not use the app.</p>

        <h2>eligibility</h2>
        <p>you must be at least 13 years old to use citymaps. by using the app, you confirm that you meet this requirement.</p>

        <h2>your account</h2>
        <p>you are responsible for your account and everything that happens under it. keep your login credentials secure. if you believe your account has been compromised, contact <a href="mailto:support@citymaps.app" className="inline-link">support@citymaps.app</a> immediately.</p>

        <h2>your content</h2>
        <p>you own the content you post on citymaps (ratings, comments, photos, lists). by posting, you grant us a non-exclusive, worldwide license to display and distribute that content within the app to provide the service.</p>
        <p>you may not post content that is illegal, harassing, hateful, sexually explicit, fraudulent, or that infringes on someone else's rights. we may remove content that violates these rules at our discretion.</p>

        <h2>acceptable use</h2>
        <p>you agree not to misuse the app. this includes attempting to access other users' accounts, scraping data, reverse-engineering the app, sending spam, or interfering with the service in any way.</p>

        <h2>termination</h2>
        <p>you can delete your account at any time from within the app. we may suspend or terminate your account if you violate these terms or engage in conduct that is harmful to other users or the service.</p>

        <h2>the service</h2>
        <p>citymaps is provided "as is." we do our best to keep it running smoothly but make no guarantees about uptime or availability. we may change, suspend, or discontinue features at any time.</p>

        <h2>limitation of liability</h2>
        <p>to the fullest extent permitted by law, citymaps and CityMaps Inc. are not liable for any indirect, incidental, or consequential damages arising from your use of the app.</p>

        <h2>disputes</h2>
        <p>these terms are governed by the laws of the state of New York. any disputes will be resolved in the courts of New York, NY.</p>

        <h2>changes</h2>
        <p>we may update these terms from time to time. if we make material changes, we will notify you in the app or by email. continued use of citymaps after changes take effect means you accept the updated terms.</p>

        <h2>contact</h2>
        <p>questions about these terms: <a href="mailto:support@citymaps.app" className="inline-link">support@citymaps.app</a>.</p>
      </div>
    </Layout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        {/* <Route path="/join" element={<Waitlist />} /> */}
        <Route path="/about" element={<About />} />
        <Route path="/support" element={<Support />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
